// src/components/AnalyticsDashboard.tsx
import React, { useState, useEffect } from 'react';
import { Users, Building2, Mic, Keyboard, TrendingUp, UserCheck, FileAudio, Sparkles, Activity, Languages, Highlighter, Clock, FileText, LogIn, LogOut, ClipboardList, Star, MessageSquare, BarChart3, Hospital } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';
import './AnalyticsDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

// Hospital domain to name mapping (excluding @careshiftai.com)
const HOSPITAL_DOMAINS: { [key: string]: string } = {
  'memorialhermann.org': 'Memorial Hermann',
  'lifepointhealth.net': 'Lifepoint Health',
  'harrishealth.org': 'Harris Health',
  'uth.tmc.edu': 'Texas Medical Center (Houston)',
  'uhsinc.com': 'Universal Health Services',
  'polarishealthde.com': 'Polaris Healthcare',
  'hardinmedical.com': 'Hardin Medical Center',
  'providence.org': 'Providence Health',
  'stewartresidential.com': 'Stewart Residential',
};

// Count of hospitals (excluding @careshiftai.com)
const HOSPITAL_COUNT = Object.keys(HOSPITAL_DOMAINS).length;

interface DepartmentData {
  department: string;
  count: number;
}

interface ActivityTypeData {
  activity_type: string;
  count: number;
}

interface DailyUsageData {
  date: string;
  avg_hours: number;
}

interface HandoffTypeData {
  handoff_type: string;
  count: number;
}

interface LanguageData {
  language: string;
  count: number;
}

interface UserOption {
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
}

interface SessionActivity {
  user_email: string;
  user_name: string;
  timestamp: string | null;
  event_type: 'login' | 'logout';
  duration_hours: number | null;
}

interface FeedbackEntry {
  id: number;
  submitter_name: string;
  email: string;
  department: string | null;
  page: string | null;
  rating: number | null;
  message: string | null;
  timestamp: string | null;
}

interface AnalyticsDashboardProps {
  onLogout: () => void;
  userName: string;
  userEmail?: string;
  isNurseAdmin?: boolean;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ onLogout, userName, userEmail, isNurseAdmin }) => {
  const { theme } = useTheme();
  const [userCount, setUserCount] = useState<number | null>(null);
  const [patientCount, setPatientCount] = useState<number | null>(null);
  const [departmentData, setDepartmentData] = useState<DepartmentData[]>([]);
  const [activityTypeData, setActivityTypeData] = useState<ActivityTypeData[]>([]);
  const [dailyUsageData, setDailyUsageData] = useState<DailyUsageData[]>([]);
  const [overallAverage, setOverallAverage] = useState<number>(0);
  const [handoffTypeData, setHandoffTypeData] = useState<HandoffTypeData[]>([]);
  const [languageData, setLanguageData] = useState<LanguageData[]>([]);
  const [avgActivitiesPerPatient, setAvgActivitiesPerPatient] = useState<number | null>(null);
  const [highlightCount, setHighlightCount] = useState<number | null>(null);
  const [reportCount, setReportCount] = useState<number | null>(null);
  const [reportDepartmentData, setReportDepartmentData] = useState<DepartmentData[]>([]);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [totalRatings, setTotalRatings] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  
  // User filter state
  const [usersList, setUsersList] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('general');
  
  // Hospital filter state (only for super admin, not nurse admin)
  const [selectedHospital, setSelectedHospital] = useState<string>('all');
  
  // Session activity state
  const [sessionActivity, setSessionActivity] = useState<SessionActivity[]>([]);
  
  // Tab state for Metrics vs Reviews
  const [activeTab, setActiveTab] = useState<'metrics' | 'reviews'>('metrics');
  
  // Feedback/Reviews state
  const [feedbackList, setFeedbackList] = useState<FeedbackEntry[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Extract domain from nurse admin's email for filtering
  const nurseAdminDomain = isNurseAdmin && userEmail ? userEmail.split('@')[1] : null;
  
  // Determine the effective domain filter (nurse admin domain or selected hospital)
  const effectiveDomain = nurseAdminDomain || (selectedHospital !== 'all' ? selectedHospital : null);

  // Fetch users list on mount (filtered by domain for nurse admins or selected hospital)
  useEffect(() => {
    const fetchUsersList = async () => {
      try {
        // Filter users by domain (nurse admin domain or selected hospital)
        const domainParam = effectiveDomain ? `?domain=${encodeURIComponent(effectiveDomain)}` : '';
        const response = await fetch(`${BACKEND_URL}/analytics/users-list${domainParam}`);
        const data = await response.json();
        if (data.success) {
          setUsersList(data.users);
        }
      } catch (error) {
        console.error('Error fetching users list:', error);
      }
    };
    
    fetchUsersList();
  }, [effectiveDomain]);

  // Fetch session activity based on selected user (and domain filter)
  useEffect(() => {
    const fetchSessionActivity = async () => {
      try {
        const isUserSpecific = selectedUser !== 'general';
        let params = 'limit=50';
        if (isUserSpecific) {
          params += `&user_email=${encodeURIComponent(selectedUser)}`;
        } else if (effectiveDomain) {
          // For nurse admins or hospital filter, filter by domain
          params += `&domain=${encodeURIComponent(effectiveDomain)}`;
        }
        const response = await fetch(`${BACKEND_URL}/analytics/session-activity?${params}`);
        const data = await response.json();
        if (data.success) {
          setSessionActivity(data.sessions);
        }
      } catch (error) {
        console.error('Error fetching session activity:', error);
      }
    };
    
    fetchSessionActivity();
  }, [selectedUser, effectiveDomain]);

  // Fetch analytics data based on selected user (and domain filter)
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const isUserSpecific = selectedUser !== 'general';
        const userPath = isUserSpecific ? `/user/${encodeURIComponent(selectedUser)}` : '';
        // Add domain filter for nurse admin or hospital filter
        const domainParam = (!isUserSpecific && effectiveDomain) ? `?domain=${encodeURIComponent(effectiveDomain)}` : '';

        // Fetch user count (only for general view)
        if (!isUserSpecific) {
          const userResponse = await fetch(`${BACKEND_URL}/analytics/user-count${domainParam}`);
          const userData = await userResponse.json();
          if (userData.success) {
            setUserCount(userData.count);
          }
        } else {
          setUserCount(1); // Single user selected
        }

        // Fetch patient count
        const patientResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/patient-count${domainParam}`);
        const patientData = await patientResponse.json();
        if (patientData.success) {
          setPatientCount(patientData.count);
        }

        // Fetch average activities per patient
        const avgActivitiesResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/avg-activities-per-patient${domainParam}`);
        const avgActivitiesData = await avgActivitiesResponse.json();
        if (avgActivitiesData.success) {
          setAvgActivitiesPerPatient(avgActivitiesData.average);
        }

        // Fetch highlight count
        const highlightResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/highlight-count${domainParam}`);
        const highlightData = await highlightResponse.json();
        if (highlightData.success) {
          setHighlightCount(highlightData.count);
        }

        // Fetch patients by department
        const deptResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/patients-by-department${domainParam}`);
        const deptData = await deptResponse.json();
        if (deptData.success) {
          setDepartmentData(deptData.data);
        }

        // Fetch activities by type
        const activityResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/activities-by-type${domainParam}`);
        const activityData = await activityResponse.json();
        if (activityData.success) {
          setActivityTypeData(activityData.data);
        }

        // Fetch daily usage data
        const usageResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/daily-usage${domainParam}`);
        const usageData = await usageResponse.json();
        if (usageData.success) {
          setDailyUsageData(usageData.data.daily_averages);
          setOverallAverage(usageData.data.overall_average);
        }

        // Fetch handoffs by type
        const handoffResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/handoffs-by-type${domainParam}`);
        const handoffData = await handoffResponse.json();
        if (handoffData.success) {
          setHandoffTypeData(handoffData.data);
        }

        // Fetch audio handoffs by language
        const languageResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/audio-handoffs-by-language${domainParam}`);
        const langData = await languageResponse.json();
        if (langData.success) {
          setLanguageData(langData.data);
        }

        // Fetch report count
        const reportCountResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/report-count${domainParam}`);
        const reportCountData = await reportCountResponse.json();
        if (reportCountData.success) {
          setReportCount(reportCountData.count);
        }

        // Fetch reports by department
        const reportDeptResponse = await fetch(`${BACKEND_URL}/analytics${userPath}/reports-by-department${domainParam}`);
        const reportDeptData = await reportDeptResponse.json();
        if (reportDeptData.success) {
          setReportDepartmentData(reportDeptData.data);
        }

        // Fetch average rating
        const ratingResponse = await fetch(`${BACKEND_URL}/analytics/average-rating${domainParam}`);
        const ratingData = await ratingResponse.json();
        if (ratingData.success) {
          setAverageRating(ratingData.average_rating);
          setTotalRatings(ratingData.total_ratings);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [selectedUser, effectiveDomain]);

  // Fetch feedback when Reviews tab is active (only for non-nurse-admin)
  useEffect(() => {
    if (activeTab !== 'reviews' || isNurseAdmin) return;
    
    const fetchFeedback = async () => {
      setFeedbackLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/analytics/all-feedback`);
        const data = await response.json();
        if (data.success) {
          setFeedbackList(data.feedback);
        }
      } catch (error) {
        console.error('Error fetching feedback:', error);
      } finally {
        setFeedbackLoading(false);
      }
    };
    
    fetchFeedback();
  }, [activeTab, isNurseAdmin]);

  // Calculate max count for bar scaling
  const maxDeptCount = Math.max(...departmentData.map(d => d.count), 1);
  const maxReportDeptCount = Math.max(...reportDepartmentData.map(d => d.count), 1);

  // Ensure both voice and typed are always shown
  // Note: Database stores 'audio' and 'text' as activity types
  const getActivityCounts = () => {
    const voiceData = activityTypeData.find(a => a.activity_type.toLowerCase() === 'audio');
    const typedData = activityTypeData.find(a => a.activity_type.toLowerCase() === 'text');
    return {
      voice: voiceData?.count || 0,
      typed: typedData?.count || 0
    };
  };
  const activityCounts = getActivityCounts();
  const maxActivityCount = Math.max(activityCounts.voice, activityCounts.typed, 1);

  // Get handoff counts by type
  const getHandoffCounts = () => {
    const audioData = handoffTypeData.find(h => h.handoff_type === 'Recorded Audio');
    const generatedData = handoffTypeData.find(h => h.handoff_type === 'Generated by CareShift');
    return {
      audio: audioData?.count || 0,
      generated: generatedData?.count || 0
    };
  };
  const handoffCounts = getHandoffCounts();
  const maxHandoffCount = Math.max(handoffCounts.audio, handoffCounts.generated, 1);

  // Get max language count for bar scaling
  const maxLanguageCount = Math.max(...languageData.map(l => l.count), 1);

  return (
    <div className={`analytics-dashboard ${theme === 'dark' ? 'dark' : ''}`} data-theme={theme}>
      <header className="analytics-header">
        <div className="analytics-header-left">
          <img src="/logo.png" alt="CareShift Logo" className="analytics-logo" />
          <h1><span className="header-text">CareShift</span> Analytics</h1>
        </div>
        <div className="analytics-header-right">
          <span className="analytics-user">Welcome, {userName}</span>
          <ThemeToggle />
          <button className="analytics-logout-btn" onClick={onLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* User Filter Dropdown, Hospital Filter, and Tab System */}
      <div className="analytics-filter-bar">
        <div className="filter-group">
          <label htmlFor="user-filter">View Analytics For:</label>
          <select
            id="user-filter"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="user-filter-dropdown"
          >
            <option value="general">General (All Users)</option>
            {usersList.map((user) => (
              <option key={user.email} value={user.email}>
                {user.full_name} ({user.email})
              </option>
            ))}
          </select>
          
          {/* Hospital Filter - Only show for super admin (not nurse admin) */}
          {!isNurseAdmin && (
            <>
              <label htmlFor="hospital-filter">Filter by Hospital:</label>
              <select
                id="hospital-filter"
                value={selectedHospital}
                onChange={(e) => {
                  setSelectedHospital(e.target.value);
                  setSelectedUser('general'); // Reset user filter when hospital changes
                }}
                className="user-filter-dropdown"
              >
                <option value="all">All Hospitals</option>
                {Object.entries(HOSPITAL_DOMAINS).map(([domain, name]) => (
                  <option key={domain} value={domain}>
                    {name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        
        {/* Tab System - Only show for non-nurse-admin */}
        {!isNurseAdmin && (
          <div className="analytics-tabs">
            <button
              className={`analytics-tab ${activeTab === 'metrics' ? 'active' : ''}`}
              onClick={() => setActiveTab('metrics')}
            >
              <BarChart3 size={18} />
              <span>Metrics</span>
            </button>
            <button
              className={`analytics-tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              <MessageSquare size={18} />
              <span>Reviews</span>
            </button>
          </div>
        )}
      </div>

      <main className="analytics-content">
        {/* Metrics Tab Content */}
        {(activeTab === 'metrics' || isNurseAdmin) && (
          <>
        {/* Top Row - 4 cards */}
        <div className="analytics-metrics">
          {/* Hospital Count - Only show for super admin in general view with no hospital filter */}
          {selectedUser === 'general' && !isNurseAdmin && selectedHospital === 'all' && (
            <div className="metric-card">
              <div className="metric-icon metric-icon-hospital">
                <Hospital size={32} />
              </div>
              <div className="metric-info">
                <span className="metric-value">{HOSPITAL_COUNT}</span>
                <span className="metric-label">Partner Hospitals</span>
              </div>
            </div>
          )}
          {selectedUser === 'general' && (
            <div className="metric-card">
              <div className="metric-icon">
                <Users size={32} />
              </div>
              <div className="metric-info">
                <span className="metric-value">
                  {loading ? '...' : userCount}
                </span>
                <span className="metric-label">Registered Users</span>
              </div>
            </div>
          )}
          <div className="metric-card">
            <div className="metric-icon metric-icon-alt">
              <UserCheck size={32} />
            </div>
            <div className="metric-info">
              <span className="metric-value">
                {loading ? '...' : patientCount}
              </span>
              <span className="metric-label">Active Patients</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon metric-icon-activity">
              <Activity size={32} />
            </div>
            <div className="metric-info">
              <span className="metric-value">
                {loading ? '...' : avgActivitiesPerPatient}
              </span>
              <span className="metric-label">Avg Activities/Patient</span>
            </div>
          </div>
          {/* Audio Handoffs - Show in first row when hospital filter is applied (to keep 4 cards) */}
          {(selectedHospital !== 'all' || isNurseAdmin || selectedUser !== 'general') && (
            <div className="metric-card">
              <div className="metric-icon metric-icon-audio">
                <FileAudio size={32} />
              </div>
              <div className="metric-info">
                <span className="metric-value">
                  {loading ? '...' : handoffCounts.audio}
                </span>
                <span className="metric-label">Audio Handoffs Analyzed</span>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Row - 4 or 5 cards depending on filter */}
        <div className="analytics-metrics analytics-metrics-row2">
          {/* Audio Handoffs - Show in second row only when no hospital filter (all hospitals view) */}
          {selectedUser === 'general' && !isNurseAdmin && selectedHospital === 'all' && (
            <div className="metric-card">
              <div className="metric-icon metric-icon-audio">
                <FileAudio size={32} />
              </div>
              <div className="metric-info">
                <span className="metric-value">
                  {loading ? '...' : handoffCounts.audio}
                </span>
                <span className="metric-label">Audio Handoffs Analyzed</span>
              </div>
            </div>
          )}
          <div className="metric-card">
            <div className="metric-icon metric-icon-highlight">
              <Highlighter size={32} />
            </div>
            <div className="metric-info">
              <span className="metric-value">
                {loading ? '...' : highlightCount}
              </span>
              <span className="metric-label">Critical Info Highlights</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon metric-icon-report">
              <FileText size={32} />
            </div>
            <div className="metric-info">
              <span className="metric-value">
                {loading ? '...' : reportCount}
              </span>
              <span className="metric-label">Reports Submitted</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon metric-icon-rating">
              <Star size={32} />
            </div>
            <div className="metric-info">
              <span className="metric-value">
                {loading ? '...' : averageRating > 0 ? `${averageRating}/5` : 'N/A'}
              </span>
              <span className="metric-label">App Rating ({totalRatings})</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon metric-icon-time">
              <Clock size={32} />
            </div>
            <div className="metric-info">
              <span className="metric-value">
                {loading ? '...' : `${overallAverage.toFixed(1)}h`}
              </span>
              <span className="metric-label">Avg Session Time</span>
            </div>
          </div>
        </div>

        {/* Charts Row 1: Patients by Department + Activity Input Method */}
        <div className="charts-row-2col">
          {/* Department Chart */}
          <div className="chart-container chart-container-horizontal">
            <div className="chart-header">
              <Building2 size={24} />
              <h2>Patients by Department</h2>
            </div>
            <div className="chart-content">
              {loading ? (
                <p className="chart-loading">Loading...</p>
              ) : departmentData.length === 0 ? (
                <p className="chart-empty">No patient data available</p>
              ) : (
                <div className="bar-chart">
                  {departmentData.map((dept, index) => (
                    <div key={dept.department} className="bar-row">
                      <span className="bar-label">{dept.department}</span>
                      <div className="bar-container">
                        <div 
                          className="bar-fill" 
                          style={{ 
                            width: `${(dept.count / maxDeptCount) * 100}%`,
                            animationDelay: `${index * 0.1}s`
                          }}
                        />
                        <span className="bar-value">{dept.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Activity Type Chart - Vertical */}
          <div className="chart-container chart-container-vertical">
            <div className="chart-header">
              <Mic size={24} />
              <h2>Activity Input Method</h2>
            </div>
            <div className="chart-content chart-content-vertical">
              {loading ? (
                <p className="chart-loading">Loading...</p>
              ) : (
                <div className="vertical-bar-chart">
                  <div className="vertical-bar-wrapper">
                    <div className="vertical-bar-column">
                      <div className="vertical-bar-track">
                        <div className="vertical-bar-inner">
                          <span className="vertical-bar-value">{activityCounts.voice}</span>
                          <div 
                            className="vertical-bar-fill" 
                            style={{ height: `${Math.max((activityCounts.voice / maxActivityCount) * 200, 8)}px` }}
                          />
                        </div>
                      </div>
                      <div className="vertical-bar-label">
                        <Mic size={20} />
                        <span>Voice</span>
                      </div>
                    </div>
                    <div className="vertical-bar-column">
                      <div className="vertical-bar-track">
                        <div className="vertical-bar-inner">
                          <span className="vertical-bar-value">{activityCounts.typed}</span>
                          <div 
                            className="vertical-bar-fill vertical-bar-fill-alt" 
                            style={{ height: `${Math.max((activityCounts.typed / maxActivityCount) * 200, 8)}px` }}
                          />
                        </div>
                      </div>
                      <div className="vertical-bar-label">
                        <Keyboard size={20} />
                        <span>Typed</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 2: Reports by Department + Handoff Submission Method */}
        <div className="charts-row-2col">
          {/* Reports by Department Chart */}
          <div className="chart-container chart-container-horizontal">
            <div className="chart-header">
              <FileText size={24} />
              <h2>Reports by Department</h2>
            </div>
            <div className="chart-content">
              {loading ? (
                <p className="chart-loading">Loading...</p>
              ) : reportDepartmentData.length === 0 ? (
                <p className="chart-empty">No report data available</p>
              ) : (
                <div className="bar-chart">
                  {reportDepartmentData.map((dept, index) => (
                    <div key={dept.department} className="bar-row">
                      <span className="bar-label">{dept.department}</span>
                      <div className="bar-container">
                        <div 
                          className="bar-fill bar-fill-report" 
                          style={{ 
                            width: `${(dept.count / maxReportDeptCount) * 100}%`,
                            animationDelay: `${index * 0.1}s`
                          }}
                        />
                        <span className="bar-value">{dept.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Handoff Type Chart - Vertical */}
          <div className="chart-container chart-container-vertical">
            <div className="chart-header">
              <FileAudio size={24} />
              <h2>Handoff Submission Method</h2>
            </div>
            <div className="chart-content chart-content-vertical">
              {loading ? (
                <p className="chart-loading">Loading...</p>
              ) : (
                <div className="vertical-bar-chart">
                  <div className="vertical-bar-wrapper">
                    <div className="vertical-bar-column">
                      <div className="vertical-bar-track">
                        <div className="vertical-bar-inner">
                          <span className="vertical-bar-value">{handoffCounts.audio}</span>
                          <div 
                            className="vertical-bar-fill vertical-bar-fill-orange" 
                            style={{ height: `${Math.max((handoffCounts.audio / maxHandoffCount) * 200, 8)}px` }}
                          />
                        </div>
                      </div>
                      <div className="vertical-bar-label">
                        <FileAudio size={20} />
                        <span>Audio</span>
                      </div>
                    </div>
                    <div className="vertical-bar-column">
                      <div className="vertical-bar-track">
                        <div className="vertical-bar-inner">
                          <span className="vertical-bar-value">{handoffCounts.generated}</span>
                          <div 
                            className="vertical-bar-fill vertical-bar-fill-purple" 
                            style={{ height: `${Math.max((handoffCounts.generated / maxHandoffCount) * 200, 8)}px` }}
                          />
                        </div>
                      </div>
                      <div className="vertical-bar-label">
                        <Sparkles size={20} />
                        <span>Generated</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Daily Usage Chart - Full Width */}
        <div className="charts-row-full">
          {/* Daily Usage Line Chart */}
          <div className="chart-container">
            <div className="chart-header">
              <TrendingUp size={24} />
              <h2>Average Daily Usage Time</h2>
            </div>
            <div className="chart-content">
              {loading ? (
                <p className="chart-loading">Loading...</p>
              ) : dailyUsageData.length === 0 ? (
                <p className="chart-empty">No usage data available</p>
              ) : (
                <div className="line-chart">
                  <div className="line-chart-container">
                    {/* Y-axis labels */}
                    <div className="line-chart-y-axis">
                      {(() => {
                        const maxHours = Math.max(...dailyUsageData.map(d => d.avg_hours), overallAverage, 1);
                        const yMax = Math.ceil(maxHours / 5) * 5 || 5;
                        return [yMax, Math.round(yMax * 0.75), Math.round(yMax * 0.5), Math.round(yMax * 0.25), 0].map((val, i) => (
                          <span key={i} className="y-label">{val}h</span>
                        ));
                      })()}
                    </div>
                    
                    {/* Chart area */}
                    <div className="line-chart-area">
                      {/* Overall average line */}
                      {(() => {
                        const maxHours = Math.max(...dailyUsageData.map(d => d.avg_hours), overallAverage, 1);
                        const yMax = Math.ceil(maxHours / 5) * 5 || 5;
                        const avgPercent = (overallAverage / yMax) * 100;
                        return (
                          <div 
                            className="average-line" 
                            style={{ bottom: `${avgPercent}%` }}
                          >
                            <span className="average-label">Avg: {overallAverage.toFixed(1)}h</span>
                          </div>
                        );
                      })()}
                      
                      {/* SVG for line and points */}
                      <svg className="line-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {(() => {
                          const maxHours = Math.max(...dailyUsageData.map(d => d.avg_hours), overallAverage, 1);
                          const yMax = Math.ceil(maxHours / 5) * 5 || 5;
                          const points = dailyUsageData.map((d, i) => {
                            const x = dailyUsageData.length > 1 
                              ? (i / (dailyUsageData.length - 1)) * 100 
                              : 50;
                            const y = 100 - (d.avg_hours / yMax) * 100;
                            return { x, y, data: d };
                          });
                          
                          // Create path
                          const pathD = points.length > 0 
                            ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
                            : '';
                          
                          return (
                            <>
                              {/* Line */}
                              <path 
                                d={pathD} 
                                fill="none" 
                                stroke="url(#lineGradient)" 
                                strokeWidth="0.5"
                                className="chart-line"
                              />
                              {/* Gradient definition */}
                              <defs>
                                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#14b8a6" />
                                  <stop offset="100%" stopColor="#3b82f6" />
                                </linearGradient>
                              </defs>
                            </>
                          );
                        })()}
                      </svg>
                      
                      {/* Data points */}
                      <div className="line-chart-points">
                        {(() => {
                          const maxHours = Math.max(...dailyUsageData.map(d => d.avg_hours), overallAverage, 1);
                          const yMax = Math.ceil(maxHours / 5) * 5 || 5;
                          return dailyUsageData.map((d, i) => {
                            const x = dailyUsageData.length > 1 
                              ? (i / (dailyUsageData.length - 1)) * 100 
                              : 50;
                            const y = (d.avg_hours / yMax) * 100;
                            return (
                              <div 
                                key={d.date}
                                className="data-point"
                                style={{ left: `${x}%`, bottom: `${y}%` }}
                                title={`${d.date}: ${d.avg_hours.toFixed(1)} hrs`}
                              >
                                <div className="point-tooltip">
                                  <span className="tooltip-date">{new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  <span className="tooltip-value">{d.avg_hours.toFixed(1)} hrs</span>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {/* X-axis labels */}
                  <div className="line-chart-x-axis">
                    {dailyUsageData.map((d, i) => {
                      // Show fewer labels if there are many data points
                      const showLabel = dailyUsageData.length <= 7 || i % Math.ceil(dailyUsageData.length / 7) === 0;
                      return showLabel ? (
                        <span 
                          key={d.date} 
                          className="x-label"
                          style={{ 
                            left: dailyUsageData.length > 1 
                              ? `${(i / (dailyUsageData.length - 1)) * 100}%` 
                              : '50%' 
                          }}
                        >
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Audio Handoffs by Language Chart - Full Width */}
        <div className="charts-row-full">
          <div className="chart-container">
          <div className="chart-header">
            <Languages size={24} />
            <h2>Audio Handoffs by Language</h2>
          </div>
          <div className="chart-content">
            {loading ? (
              <p className="chart-loading">Loading...</p>
            ) : (
              <div className="language-bar-chart">
                {languageData.map((lang) => (
                  <div key={lang.language} className="language-bar-item">
                    <div className="language-bar-track">
                      <div className="language-bar-inner">
                        <span className="language-bar-value">{lang.count}</span>
                        <div 
                          className="language-bar-fill"
                          style={{ height: `${Math.max((lang.count / maxLanguageCount) * 150, 8)}px` }}
                        />
                      </div>
                    </div>
                    <span className="language-bar-label">{lang.language}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* User Session Activity Log - Full Width */}
        <div className="charts-row-full">
          <div className="chart-container session-activity-container">
            <div className="chart-header">
              <ClipboardList size={24} />
              <h2>User Session Activity</h2>
            </div>
            <div className="chart-content">
              {loading ? (
                <p className="chart-loading">Loading...</p>
              ) : sessionActivity.length === 0 ? (
                <p className="chart-empty">No session activity recorded yet.</p>
              ) : (
                <div className="session-activity-log">
                  {(() => {
                    // Build a set of currently active users (logged in but not logged out)
                    // Process sessions in reverse chronological order to find users whose most recent event is login
                    const activeUsers = new Set<string>();
                    const processedUsers = new Set<string>();
                    
                    // Sessions are already sorted by timestamp desc, so first occurrence is most recent
                    sessionActivity.forEach(session => {
                      if (!processedUsers.has(session.user_email)) {
                        processedUsers.add(session.user_email);
                        if (session.event_type === 'login') {
                          activeUsers.add(session.user_email);
                        }
                      }
                    });
                    
                    return sessionActivity.map((session, index) => {
                    const isLogin = session.event_type === 'login';
                    const isCurrentlyActive = isLogin && activeUsers.has(session.user_email) && 
                      // Only show active on the most recent login for this user
                      !sessionActivity.slice(0, index).some(s => s.user_email === session.user_email);
                    
                    // Format timestamp - timestamps from backend are in UTC but without 'Z' suffix
                    // Append 'Z' to tell JavaScript it's UTC, then display in user's local timezone
                    const formatTimestamp = (ts: string | null) => {
                      if (!ts) return '';
                      // Append 'Z' if no timezone indicator present to parse as UTC
                      const utcTimestamp = ts.includes('Z') || ts.includes('+') || /\d{2}-\d{2}:\d{2}$/.test(ts) ? ts : ts + 'Z';
                      const date = new Date(utcTimestamp);
                      return date.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                    };
                    
                    // Format duration (hours to hours and minutes)
                    const formatDuration = (hours: number | null) => {
                      if (hours === null || hours === undefined) return '';
                      const totalMinutes = Math.round(hours * 60);
                      const hrs = Math.floor(totalMinutes / 60);
                      const mins = totalMinutes % 60;
                      if (hrs > 0) {
                        return `(${hrs} hour${hrs !== 1 ? 's' : ''}, ${mins} minute${mins !== 1 ? 's' : ''} spent total)`;
                      }
                      return `(${mins} minute${mins !== 1 ? 's' : ''} spent total)`;
                    };
                    
                    return (
                      <div key={`${session.user_email}-${session.timestamp}-${index}`} className={`session-activity-item ${isLogin ? 'login' : 'logout'}`}>
                        <div className="session-icon">
                          {isLogin ? <LogIn size={18} /> : <LogOut size={18} />}
                        </div>
                        <div className="session-details">
                          <span className="session-timestamp">{formatTimestamp(session.timestamp)}</span>
                          <span className="session-message">
                            <strong>{session.user_name}</strong> logged {isLogin ? 'in' : 'out'}.
                            {!isLogin && session.duration_hours !== null && (
                              <span className="session-duration"> {formatDuration(session.duration_hours)}</span>
                            )}
                            {isCurrentlyActive && (
                              <span className="session-active-indicator">● Active Now</span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  });
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}

        {/* Reviews Tab Content - Only for non-nurse-admin */}
        {activeTab === 'reviews' && !isNurseAdmin && (
          <div className="reviews-tab-content">
            <div className="reviews-header">
              <MessageSquare size={24} />
              <h2>User Reviews & Feedback</h2>
              <span className="reviews-count">({feedbackList.length} reviews)</span>
            </div>
            <div className="reviews-list">
              {feedbackLoading ? (
                <p className="reviews-loading">Loading reviews...</p>
              ) : feedbackList.length === 0 ? (
                <p className="reviews-empty">No reviews submitted yet.</p>
              ) : (
                (() => {
                  // Group reviews by date and add separators
                  const getDateKey = (ts: string | null) => {
                    if (!ts) return 'Unknown';
                    const date = new Date(ts);
                    return date.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    });
                  };
                  
                  const formatTimestamp = (ts: string | null) => {
                    if (!ts) return '';
                    const date = new Date(ts);
                    return date.toLocaleString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });
                  };
                  
                  let lastDateKey = '';
                  
                  return feedbackList.map((feedback) => {
                    const currentDateKey = getDateKey(feedback.timestamp);
                    const showDateSeparator = currentDateKey !== lastDateKey;
                    lastDateKey = currentDateKey;
                    
                    return (
                      <React.Fragment key={feedback.id}>
                        {showDateSeparator && (
                          <div className="review-date-separator">
                            <span className="review-date-text">{currentDateKey}</span>
                          </div>
                        )}
                        <div className="review-card">
                          <div className="review-header">
                            <div className="review-submitter">
                              <strong>{feedback.submitter_name}</strong>
                              {feedback.rating && (
                                <div className="review-rating">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      size={16}
                                      className={star <= feedback.rating! ? 'star-filled' : 'star-empty'}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className="review-timestamp">{formatTimestamp(feedback.timestamp)}</span>
                          </div>
                          <div className="review-meta">
                            {feedback.department && (
                              <span className="review-meta-item">
                                <Building2 size={14} />
                                {feedback.department}
                              </span>
                            )}
                            {feedback.page && (
                              <span className="review-meta-item">
                                <FileText size={14} />
                                {feedback.page}
                              </span>
                            )}
                          </div>
                          {feedback.message && (
                            <div className="review-message">
                              <p>{feedback.message}</p>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  });
                })()
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
